// Ported from EnterpriseTemplates react-project-template (Release 1.3.1), PMS slice 4b.
// ============================================================================
// ORGANISATION FORM MODAL — thin EnterpriseFormModal shell driven by a definition
// ============================================================================
// Create posts the kind's create command; edit patches what the master lets an
// application change (name, status, parent). The API writes through to the
// organisation master first; a refusal arrives as the envelope's message.

import React, { useCallback } from 'react';
import { EnterpriseFormModal, type FormModalMode } from 'components/enterprise-shell';
import { organisationService } from 'api/services/organisationService';
import type { OrganisationLookups } from 'api/types/organisation.types';
import type { OrganisationRow, OrganisationScreenDefinition } from './organisationScreen.types';

interface OrganisationFormModalProps<TRecord extends OrganisationRow, TForm extends object> {
  definition: OrganisationScreenDefinition<TRecord, TForm>;
  lookups: OrganisationLookups;
  isOpen: boolean;
  mode: FormModalMode;
  record: TRecord | null;
  tabId?: string;
  moduleName?: string;
  createInstanceId?: string;
  onClose: () => void;
  onSaved: () => void;
  onRestoreModal: (mode: FormModalMode, instanceId?: string, recordCode?: string) => void;
}

export function OrganisationFormModal<TRecord extends OrganisationRow, TForm extends object>({
  definition, lookups, isOpen, mode, record, tabId, moduleName, createInstanceId,
  onClose, onSaved, onRestoreModal,
}: OrganisationFormModalProps<TRecord, TForm>): React.ReactElement | null {
  const { kind, noun, emptyForm, populate, validate, toCreate, toUpdate, renderFields, recordCode, recordName, formSize } = definition;

  const handleSave = useCallback(async (form: TForm, m: FormModalMode, rec: TRecord | null) => {
    if (m === 'edit' && rec) {
      const res = await organisationService.update<TRecord>(kind, rec.id, toUpdate(form, rec));
      if (!res.success) throw new Error(res.message);
    } else {
      const res = await organisationService.create<TRecord>(kind, toCreate(form));
      if (!res.success) throw new Error(res.message);
    }
  }, [kind, toCreate, toUpdate]);

  return (
    <EnterpriseFormModal<TForm, TRecord>
      isOpen={isOpen}
      mode={mode}
      record={record}
      noun={noun}
      emptyForm={emptyForm}
      populateOnOpen={populate}
      validate={validate}
      onSave={handleSave}
      size={formSize ?? 'md'}
      tabId={tabId}
      moduleName={moduleName || definition.moduleName}
      createInstanceId={createInstanceId}
      recordKey={record ? recordCode(record) : undefined}
      getStripRecordLabel={(rec) => (rec ? `${recordCode(rec)} - ${recordName(rec)}` : undefined)}
      onClose={onClose}
      onSaved={onSaved}
      onRestoreModal={onRestoreModal}
    >
      {(args) => renderFields({ ...args, lookups })}
    </EnterpriseFormModal>
  );
}

export default OrganisationFormModal;
